import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { GradientText } from './ui/GradientText';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { ArrowRight } from 'lucide-react';

export default function Hero() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const heroRef = useRef<HTMLDivElement>(null);

  // Animation subtile lorsque la section est chargée
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
        }
      });
    }, { threshold: 0.1 });

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => {
      if (heroRef.current) observer.unobserve(heroRef.current);
    };
  }, []);

  return (
    <div className="relative overflow-hidden bg-black min-h-[90vh] flex items-center" ref={heroRef}>
      {/* Fond moderne avec mesh gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(124,58,237,0.15),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(45,212,191,0.15),transparent_50%)]" />
      
      {/* Effet de cercle lumineux en arrière-plan */}
      <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-violet-500/10 to-transparent blur-3xl" />
      <div className="absolute bottom-1/4 left-1/4 w-[600px] h-[600px] rounded-full bg-gradient-to-tr from-cyan-500/10 to-transparent blur-3xl" />
      
      {/* Grille stylisée en arrière-plan */}
      <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-repeat opacity-5" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <div className="text-center md:text-left max-w-3xl mx-auto md:mx-0">
          <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-xl px-4 py-2 rounded-full mb-6 border border-white/10 text-sm text-white/80">
            <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 animate-pulse"></span>
            Botmaker AI Platform
          </div>
          
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight">
            {t('pages.home.hero.title')}{' '}
            <GradientText className="bg-gradient-to-r from-violet-500 via-indigo-400 to-cyan-400">
              {t('pages.home.hero.subtitle')}
            </GradientText>
          </h1>
          
          <p className="text-xl text-gray-300 mb-10 max-w-2xl">
            {t('pages.home.hero.description')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 md:justify-start justify-center">
            <button 
              onClick={() => navigate(user ? '/app/dashboard' : '/pricing')}
              className="group bg-gradient-to-r from-violet-600 to-cyan-500 text-white px-8 py-4 rounded-xl text-lg font-medium hover:shadow-[0_0_30px_rgba(124,58,237,0.5)] transition-all duration-300 flex items-center justify-center gap-2"
            >
              {user ? t('pages.home.hero.cta.dashboard') : t('pages.home.hero.cta.primary')}
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            
            <button 
              onClick={() => navigate('/pricing')}
              className="bg-white/5 backdrop-blur-xl text-white px-8 py-4 rounded-xl text-lg font-medium border border-white/10 hover:bg-white/10 hover:border-violet-500/50 transition-all"
            >
              {t('pages.home.hero.cta.secondary')}
            </button>
          </div>
        </div>
      </div>
      
      {/* Séparateur subtil en bas */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-violet-500 to-transparent"></div>
    </div>
  );
}